import { useTranslation } from 'react-i18next';
import { listingBadges, type ListingBadge, type ListingBadgeInput } from '../../utils/listingBadges';

/**
 * The listing marks as chips (see utils/listingBadges.ts for when each shows).
 * Colour follows the THEME.md register: «مميز» is the gold featured flag, the
 * three popularity marks are the red "hot flag" (blush fill, red ink) told apart
 * by a line icon, and «جديد» is a teal outline on white. Renders the chips only,
 * so a card can line them up with its own status chip.
 */

const STYLE: Record<ListingBadge, string> = {
  featured: 'bg-[#FFC107] text-[#4A3800]',
  hot: 'bg-[#FDE8E7] text-[#C0322A]',
  mostLiked: 'bg-[#FDE8E7] text-[#C0322A]',
  mostViewed: 'bg-[#FDE8E7] text-[#C0322A]',
  new: 'bg-[var(--pv-color-surface)] text-[var(--pv-color-primary)] ring-1 ring-inset ring-[var(--pv-color-primary)]',
};

const ICON: Partial<Record<ListingBadge, string>> = {
  hot: 'ri-fire-line',
  mostLiked: 'ri-heart-3-line',
  mostViewed: 'ri-eye-line',
};

const SIZE = {
  // On a card photo: compact, and opaque so it reads over any picture.
  sm: 'gap-1 px-2.5 py-1 text-[0.8125rem]',
  // On the property page header, beside the FAL chip.
  md: 'gap-1.5 px-3 py-1 text-[0.8125rem]',
} as const;

export interface ListingBadgesProps {
  listing: ListingBadgeInput;
  /** How many earned marks to show; a card shows its strongest one. */
  earnedLimit?: number;
  size?: keyof typeof SIZE;
}

export default function ListingBadges({ listing, earnedLimit, size = 'sm' }: ListingBadgesProps) {
  const { t } = useTranslation('property');
  const badges = listingBadges(listing, { earnedLimit });
  if (badges.length === 0) return null;
  return (
    <>
      {badges.map((badge) => (
        <span
          key={badge}
          className={`inline-flex items-center rounded-full font-semibold leading-none ${SIZE[size]} ${STYLE[badge]}`}
        >
          {ICON[badge] && <i className={`${ICON[badge]} leading-none`} aria-hidden="true" />}
          {t(`property.badges.${badge}`)}
        </span>
      ))}
    </>
  );
}
